/**
 * "Order again" — the product page's side.
 *
 * When the customer should look a garment over before it goes in the cart, the account page's
 * "Order again" links to its product page:
 *
 *   /products/<handle>?lx_reorder=<order name, "#" optional>&lx_line=<0-based line in that order>
 *   optional: &lx_meas_order=<order name>&lx_meas_line=<line>   measurements from another line
 *             &lx_meas=0                                         the line has no measurements
 *
 * Only the made-to-measure pages load this (shirt, trousers, suit, jacket, coat). Each side of the
 * page (DT desktop, MB phone) reports in through window.LXReorderQueue once its style options are
 * in, and this loads the order line into it exactly as the drawer's own "Load from past order"
 * does: NS._loadFromOrder(order, line, 'style') and (…, 'meas'). A page can name other kinds in
 * NS._lxReorder (the suit loads its jacket and trousers separately). Then it opens the customiser
 * on screen with a short note saying where everything came from.
 *
 * - The orders are window.__luxire_orders: the signed-in customer's latest 20. If the customer
 *   isn't signed in, or the order or line isn't there, or the line is another kind of garment than
 *   the page's (NS._matchGarment), nothing is loaded and a short note says why.
 * - A line without measurements (a standard size or Expert-Guided Fit; &lx_meas=0, or seen from its
 *   properties) loads its style only, and the notes give last time's size to pick again. The page's
 *   own Add to Cart then sends the style as "From order", as the customiser's does.
 * - Each load is tried on its own: one that fails is said in the note, and if nothing loads the
 *   note says the order couldn't be loaded.
 * - The lx_ parameters leave the address as soon as this starts, so a reload or Back doesn't load
 *   the order again over the customer's changes.
 */
(function () {
  'use strict';

  var params;
  try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
  var orderParam = params.get('lx_reorder');
  if (!orderParam) return;
  var lineParam = params.get('lx_line');
  var measOrderParam = params.get('lx_meas_order');
  var measLineParam = params.get('lx_meas_line');
  var measParam = params.get('lx_meas');
  var HERE = window.location.pathname + window.location.search;   // for "Sign in", before it's tidied

  var DEFAULT_KINDS = { style: ['style'], meas: ['meas'] };
  // Old orders kept each style choice as its own line, its title starting like this; the lead-in
  // comes off for the list.
  var OLD_STYLE = /^\s*hidden internal (product|styles)/i;
  var OLD_STYLE_LEAD = /^\s*hidden internal (products?|styles)\s*[:\-–—]?\s*/i;
  var LEGACY_MEAS = /^(body|shirt|jacket|trouser|pants)-/;
  var NOUN = { shirt: 'a shirt', pants: 'trousers', suit: 'a suit', jacket: 'a jacket', outerwear: 'a coat' };

  function norm(name) { return String(name == null ? '' : name).replace(/^\s*#/, '').trim().toLowerCase(); }
  function shown(name) { var n = String(name == null ? '' : name).trim(); return n.charAt(0) === '#' ? n : '#' + n; }
  function index(value) { return /^\d+$/.test(String(value == null ? '' : value).trim()) ? parseInt(value, 10) : -1; }
  function blank(v) { return v == null || String(v).trim() === ''; }
  function propsOf(item) {
    var p = item && item.props;
    if (Array.isArray(p)) {
      var o = {};
      p.forEach(function (x) { if (Array.isArray(x) && x.length >= 2) o[x[0]] = x[1]; });
      return o;
    }
    return p || {};
  }
  function isAddon(item) { var p = propsOf(item); return !blank(p._for) || !blank(p._linked) || !blank(p._parent); }

  // Finds an order line in the customer's latest orders: {oi, li, order, item} or why it isn't there.
  function find(orderName, lineValue) {
    var orders = window.__luxire_orders || [];
    var oi = -1;
    for (var i = 0; i < orders.length; i++) { if (orders[i] && norm(orders[i].name) === norm(orderName)) { oi = i; break; } }
    if (oi < 0) return { missing: 'order' };
    var li = index(lineValue);
    var items = orders[oi].items || [];
    if (li < 0 || li >= items.length || !items[li]) return { missing: 'line', order: orders[oi] };
    return { oi: oi, li: li, order: orders[oi], item: items[li] };
  }

  // The page's own test for "a garment of this page's kind" (the suit page takes suit lines).
  function fits(item) {
    var DT = window.DT;
    if (!DT || typeof DT._matchGarment !== 'function') return true;
    try { return !!DT._matchGarment(item); } catch (e) { return true; }
  }

  // Measurements saved on the line: "Meas: …" (other than the method), old body-/shirt-… keys, or a
  // line the customiser made (_type "custom"). A standard size or Expert-Guided Fit has none.
  function hasMeasurements(item) {
    var p = propsOf(item);
    if (String(p._type || '').trim().toLowerCase() === 'custom') return true;
    return Object.keys(p).some(function (k) {
      if (blank(p[k])) return false;
      if (k.indexOf('Meas: ') === 0) return k !== 'Meas: Method';
      return LEGACY_MEAS.test(k);
    });
  }

  // Old orders kept the style choices as separate "Hidden Internal Product: …" lines. They can't be
  // loaded into the drawer, so the note lists them: their name and any properties. Add-ons (linked
  // to their garment by _for, _linked or _parent) are titled the same way today, and aren't listed.
  function oldChoices(order) {
    var out = [];
    (order.items || []).forEach(function (item) {
      var title = String((item && item.title) || '');
      if (!OLD_STYLE.test(title) || isAddon(item)) return;
      var name = title.replace(OLD_STYLE_LEAD, '').trim() || title.trim();
      var p = propsOf(item), bits = [];
      Object.keys(p).forEach(function (k) {
        if (k.charAt(0) === '_' || blank(p[k])) return;
        bits.push(k + ': ' + p[k]);
      });
      out.push(bits.length ? name + ' – ' + bits.join(' · ') : name);
    });
    return out;
  }

  function signedIn() { return !!window.__luxireCustomerId; }

  // What to load, worked out once the page has finished loading.
  function plan() {
    if (!signedIn()) return { problem: 'signin' };
    var main = find(orderParam, lineParam);
    if (main.missing === 'order') return { problem: 'order' };
    if (main.missing === 'line') return { problem: 'line', order: main.order };
    if (!fits(main.item)) return { problem: 'kind', order: main.order };
    var pl = { main: main, meas: null, measProblem: null, size: null, old: oldChoices(main.order) };
    if (measOrderParam) {
      var m = find(measOrderParam, measLineParam);
      if (m.missing) pl.measProblem = m.missing;
      else if (!fits(m.item)) pl.measProblem = 'kind';
      else if (!hasMeasurements(m.item)) pl.measProblem = 'none';
      else pl.meas = m;
    } else if (measParam !== '0' && hasMeasurements(main.item)) {
      pl.meas = main;
    }
    if (!pl.meas) {
      var mp = propsOf(main.item);
      if (!blank(mp.Size)) pl.size = String(mp.Size).trim();
      // Expert-Guided Fit isn't a size to pick: it's the "Not sure about your fit?" choice.
      pl.expert = !!pl.size && (String(mp._type || '').trim().toLowerCase() === 'expert' || /^expert/i.test(pl.size));
    }
    return pl;
  }

  // Take the lx_ parameters out of the address: a reload or Back must not load the order again.
  function tidy() {
    try {
      var q = new URLSearchParams(window.location.search);
      ['lx_reorder', 'lx_line', 'lx_meas_order', 'lx_meas_line', 'lx_meas'].forEach(function (k) { q.delete(k); });
      var s = q.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (s ? '?' + s : '') + window.location.hash);
    } catch (e) { /* an address we can't change is only a reload away from loading again */ }
  }

  // ── notes ──
  function css() {
    if (document.getElementById('lx-ro-style')) return;
    var s = document.createElement('style');
    s.id = 'lx-ro-style';
    s.textContent =
      '.lx-ro-note{display:flex;gap:10px;align-items:flex-start;margin:0 0 14px;padding:10px 12px;background:var(--gold-bg,rgba(154,118,52,.08));border:1px solid var(--gold-border,rgba(154,118,52,.2));border-radius:8px;font-family:var(--sans,system-ui,sans-serif);font-size:12px;line-height:1.5;color:var(--t1,#1a1a1a);text-align:left}' +
      '.lx-ro-note__body{flex:1;min-width:0;overflow-wrap:anywhere}' +
      '.lx-ro-note p{margin:0}' +
      '.lx-ro-note p+p,.lx-ro-note ul{margin-top:6px}' +
      '.lx-ro-note ul{margin-bottom:0;padding-left:18px}' +
      '.lx-ro-note a{color:var(--gold,#9a7634);font-weight:600;text-decoration:underline}' +
      '.lx-ro-note__x{flex-shrink:0;border:0;background:none;color:var(--t3,#888);font-size:14px;line-height:1;padding:2px 4px;cursor:pointer}';
    document.head.appendChild(s);
  }
  function p(text) { var el = document.createElement('p'); el.textContent = text; return el; }
  function note(parts, kind) {
    css();
    var box = document.createElement('div');
    box.className = 'lx-ro-note';
    box.setAttribute('role', 'status');
    box.setAttribute('data-lx-reorder-note', kind);
    var body = document.createElement('div');
    body.className = 'lx-ro-note__body';
    parts.forEach(function (x) { if (x) body.appendChild(x); });
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'lx-ro-note__x';
    x.setAttribute('aria-label', 'Dismiss');
    x.textContent = '✕';
    x.onclick = function () { if (box.parentNode) box.parentNode.removeChild(box); };
    box.appendChild(body);
    box.appendChild(x);
    return box;
  }
  function onScreen(NS) {
    var phone = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    return phone ? NS.prefix === 'mb' : NS.prefix === 'dt';
  }

  // A note next to the size choices, desktop and phone. scroll: bring the phone's into view.
  function pageNote(parts, kind, scroll) {
    ['dt-sizeCardStd', 'mb-sizeCardStd'].forEach(function (id) {
      var host = document.getElementById(id);
      if (!host || !host.parentNode) return;
      var box = note(parts.map(function (n) { return n.cloneNode(true); }), kind);
      host.parentNode.insertBefore(box, host);
      // On a phone the size choices are below the photos.
      if (scroll && id === 'mb-sizeCardStd' && onScreen({ prefix: 'mb' }) && box.scrollIntoView) box.scrollIntoView({ block: 'center' });
    });
  }

  // Nothing was loaded: say why, and leave the page as it is.
  function problemNote(pl) {
    var parts = [];
    if (pl.problem === 'signin') {
      var line = document.createElement('p');
      var a = document.createElement('a');
      a.href = '/account/login?return_url=' + encodeURIComponent(HERE);
      a.textContent = 'Sign in';
      line.appendChild(a);
      line.appendChild(document.createTextNode(' to load your order ' + shown(orderParam) + '.'));
      parts.push(line);
    } else if (pl.problem === 'order') {
      parts.push(p('We couldn’t find order ' + shown(orderParam) + ' in your recent orders, so nothing was loaded.'));
    } else if (pl.problem === 'line') {
      parts.push(p('We couldn’t find that garment in order ' + shown(pl.order.name) + ', so nothing was loaded.'));
    } else if (pl.problem === 'kind') {
      var noun = NOUN[(window.DT && window.DT._garmentType) || ''] || 'this garment';
      parts.push(p('That line in order ' + shown(pl.order.name) + ' isn’t for ' + noun + ', so nothing was loaded.'));
    } else {
      parts.push(p('We couldn’t load order ' + shown(pl.main.order.name) + ', so nothing was loaded.'));
    }
    pageNote(parts, pl.problem, true);
  }

  // Loaded (some or all of it): say what, from where, at the top of the customiser.
  function drawerNote(NS, pl, res) {
    var drawer = document.getElementById(NS.prefix + '-drawer');
    var body = drawer && drawer.querySelector('.dr-body');
    if (!body || body.querySelector('[data-lx-reorder-note]')) return;
    var parts = [];
    var from = shown(pl.main.order.name);
    var measFrom = pl.meas ? shown(pl.meas.order.name) : '';
    var other = pl.meas && norm(pl.meas.order.name) !== norm(pl.main.order.name);
    if (res.style && res.meas) parts.push(p(other ? 'Loaded from order ' + from + ', with the measurements from order ' + measFrom + '.' : 'Loaded from order ' + from + '.'));
    else if (res.style && res.meas === null) parts.push(p('Loaded the style from order ' + from + '.'));
    else if (res.style) parts.push(p('Loaded the style from order ' + from + '. We couldn’t load the measurements from order ' + measFrom + ', so please choose them.'));
    else parts.push(p('Loaded the measurements from order ' + measFrom + '. We couldn’t load the style from order ' + from + ', so please check it.'));
    if (pl.measProblem) {
      var mo = shown(measOrderParam);
      parts.push(p(pl.measProblem === 'order' ? 'We couldn’t find order ' + mo + ' in your recent orders, so its measurements weren’t loaded.'
        : pl.measProblem === 'line' ? 'We couldn’t find those measurements in order ' + mo + ', so they weren’t loaded.'
        : pl.measProblem === 'kind' ? 'The measurements in order ' + mo + ' are for a different garment, so they weren’t loaded.'
        : 'That line in order ' + mo + ' has no measurements, so none were loaded.'));
    }
    if (pl.size && res.style) parts.push(p(pl.expert ? 'Last time you chose Expert-Guided Fit – pick “Not sure about your fit?” on the page, or enter your measurements below.'
      : 'Your size last time: ' + pl.size + ' – pick it under Standard Sizes on the page, or enter your measurements below.'));
    // The page swapped a construction it no longer makes (its _lxRetire); say so up here too.
    if (NS._lxRetired) parts.push(p('We no longer offer Light Fused construction, so we’ve set Standard Half Canvas instead.'));
    if (pl.old.length) {
      parts.push(p('This order also had these choices. We can’t load them for you, so please set them again:'));
      var ul = document.createElement('ul');
      pl.old.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; ul.appendChild(li); });
      parts.push(ul);
    }
    body.insertBefore(note(parts, res.style && res.meas !== false ? 'loaded' : 'partial'), body.firstChild);
    body.scrollTop = 0;
  }

  // ── loading ──
  var pl = null, started = false, seen = [], shownNotes = false;
  // Each kind on its own, so one that fails doesn't skip the others; false if any failed.
  function attempt(NS, at, kinds) {
    var ok = true;
    kinds.forEach(function (k) {
      try { NS._loadFromOrder(at.oi, at.li, k); }
      catch (e) { ok = false; if (window.console) console.warn('[Luxire] reorder: loading ' + k + ' into ' + NS.prefix + ' failed', e); }
    });
    return ok;
  }
  function load(NS) {
    if (!pl || pl.problem || !NS || typeof NS._loadFromOrder !== 'function') return;
    var kinds = NS._lxReorder || DEFAULT_KINDS;
    var res = { style: attempt(NS, pl.main, kinds.style), meas: pl.meas ? attempt(NS, pl.meas, kinds.meas) : null };
    if (!onScreen(NS) || shownNotes) return;
    shownNotes = true;
    if (!res.style && !res.meas) { problemNote({ problem: 'failed', main: pl.main }); return; }
    if (typeof NS.openDrawer === 'function') NS.openDrawer();
    drawerNote(NS, pl, res);
    // No measurements to load: last time's size, right above the size choices.
    if (pl.size && res.style) pageNote([p(pl.expert ? 'Last time you chose Expert-Guided Fit – pick “Not sure about your fit?” below.' : 'Your size last time: ' + pl.size + ' – pick it below.')], 'size', false);
  }
  function take(NS) {
    if (!NS || seen.indexOf(NS) >= 0) return;
    seen.push(NS);
    if (started) load(NS);
  }

  var early = window.LXReorderQueue;
  window.LXReorderQueue = { push: take };
  if (early && early.length) for (var i = 0; i < early.length; i++) take(early[i]);

  function start() {
    if (started) return;
    started = true;
    tidy();
    pl = plan();
    if (pl.problem) { problemNote(pl); return; }
    seen.forEach(load);
  }
  if (document.readyState === 'complete') start();
  else {
    document.addEventListener('DOMContentLoaded', start);
    window.addEventListener('load', start);
  }
})();
